import { getDeviceStatus } from '@/api/android';
import DeviceStatusResponse from '@/api/android/interfaces/DeviceStatusResponse';
import i18n from '@/i18n';
import { defineStore } from 'pinia';
import { computed, onUnmounted, ref } from 'vue';

export const useDeviceStore = defineStore('device', () => {
  const { t } = i18n.global;

  const isSupported = ref(false);
  const deviceStatus = ref<DeviceStatusResponse | null>(null);
  const currentTime = ref('');
  const isInitialized = ref(false); // 중복 초기화 방지용

  const TIME_INTERVAL: number = 10000; // 시간 표시 업데이트 간격 (10초마다)
  const STATUS_INTERVAL: number = 60000; // 상태 업데이트 간격 (1분마다)

  let timeTimer: ReturnType<typeof setTimeout> | null = null;
  let statusTimer: ReturnType<typeof setTimeout> | null = null;

  // 배터리 충전 상태
  const batteryState = computed((): 0 | 1 | 2 | 3 | 4 => {
    const device = deviceStatus.value;
    if (!device) return 1;
    if (device.isCharging) return 0; // 상태 갱신 기간(1분)이 너무 길어 충전 여부 대신 잔량만 표시
    if (device.batteryState <= 25) return 1;
    if (device.batteryState <= 50) return 2;
    if (device.batteryState <= 75) return 3;
    return 4;
  });

  // 배터리 잔량
  const batteryPercent = computed((): number => deviceStatus.value?.batteryState ?? 0);

  // 와이파이 상태
  const wifiState = computed((): 0 | 1 | 2 | 3 | 4 => {
    const wifi = deviceStatus.value?.wifiState;
    if (!wifi || wifi === -1) return 0;
    if (wifi <= 1) return 1;
    if (wifi === 2) return 2;
    if (wifi === 3) return 3;
    return 4;
  });

  /**
   * 디바이스 상태 설정
   * @param status 디바이스 상태
   */
  const setDeviceStatus = (status: DeviceStatusResponse) => {
    isSupported.value = !!status;
    deviceStatus.value = status;
  };

  /** 시간 업데이트 */
  const updateTime = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? t('label.pm') : t('label.am');

    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, '0');
    currentTime.value = `${ampm} ${formattedHours}:${minutes}`;
  };

  /** 타이머 중지 */
  const stopTimers = () => {
    if (timeTimer) clearTimeout(timeTimer);
    if (statusTimer) clearTimeout(statusTimer);
    timeTimer = null;
    statusTimer = null;
  };

  /** 타이머 시작 */
  const startTimers = () => {
    // 중복 실행 방지: 실행 전 기존 타이머를 확실히 제거
    stopTimers();

    /** 시간 타이머 갱신 */
    const runTimeLoop = () => {
      updateTime();
      timeTimer = setTimeout(runTimeLoop, TIME_INTERVAL);
    };

    /** 상태 타이머 갱신 */
    const runStatusLoop = () => {
      const status = getDeviceStatus();
      if (status) setDeviceStatus(status);
      statusTimer = setTimeout(runStatusLoop, STATUS_INTERVAL);
    };

    runTimeLoop();
    runStatusLoop();
  };

  /** 페이지 숨김/표시 이벤트 핸들러 */
  const handleVisibilityChange = () => {
    document.hidden ? stopTimers() : startTimers();
  };

  /** 타이머 초기화 */
  const init = () => {
    if (isInitialized.value) return;
    isInitialized.value = true;

    startTimers();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    onUnmounted(() => {
      stopTimers();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });
  };

  /** 강제 상태 갱신 */
  const refreshStatus = () => {
    // 타이머 주기와 상관없이 즉시 실행
    updateTime();
    const status = getDeviceStatus();
    if (status) setDeviceStatus(status);
  };

  return {
    isSupported,
    currentTime,
    batteryState,
    batteryPercent,
    wifiState,

    init,
    refreshStatus,
  };
});
